import { formatInstant } from '@msrllabs/pt-api/utils';
import { format as formatDate, formatDistance, isAfter, isBefore, isThisYear, isToday, isYesterday, subDays } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { dateLocale } from '@/i18n';

export default function useDateFormat() {
  // subscribes callers, even memoized ones, to language changes
  const { t } = useTranslation();
  // patterns are translation keys, so a language can reorder them (e.g. Korean year-month-day)
  const format = (date: Date, pattern: string) => formatDate(date, pattern, { locale: dateLocale() });
  const ago = (date: Date) => t('{{distance}} ago', { distance: formatDistance(date, new Date(), { locale: dateLocale() }) });

  return {
    format: (date: any) => {
      const dateTime = new Date(date);
      if (isToday(dateTime)) {
        return ago(dateTime);
      } else if (isYesterday(dateTime)) {
        return format(dateTime, t("'Yesterday at' HH:mm"));
      } else if (!isThisYear(dateTime)) {
        return format(dateTime, t('d MMM y'));
      } else {
        return format(dateTime, t("d MMM 'at' HH:mm"));
      }
    },
    format2: (date: any) => {
      const dateTime = new Date(date);
      if (isToday(dateTime)) {
        // today
        return format(dateTime, 'HH:mm');
      } else if (isYesterday(dateTime)) {
        // yesterday
        return t('Yesterday');
      } else if (isAfter(dateTime, subDays(new Date(), 7)) && isBefore(dateTime, new Date())) {
        // within 7 days
        return format(dateTime, 'E');
      } else if (isThisYear(dateTime)) {
        // current year
        return format(dateTime, t('d MMM'));
      } else {
        // outside current year
        return format(dateTime, t('d MMM y'));
      }
    },
    format3: (date: any) => {
      const dateTime = new Date(date);
      if (isToday(dateTime)) {
        // today
        return t('Today');
      } else if (isYesterday(dateTime)) {
        // yesterday
        return t('Yesterday');
      } else if (isAfter(dateTime, subDays(new Date(), 7)) && isBefore(dateTime, new Date())) {
        // within 7 days
        return format(dateTime, 'EEEE');
      } else if (isThisYear(dateTime)) {
        // in current year
        return format(dateTime, t('d MMM'));
      } else {
        // before current year
        return format(dateTime, t('d MMM, y'));
      }
    },
    formatChat: (date: any) => {
      return format(new Date(date), 'HH:mm');
    },
    formatEvent: (date: any, type?: string) => {
      const dateTime = new Date(date);
      switch (type) {
        case 'FULL':
          return format(dateTime, t('HH:mm \xa0 EEEE, MMMM d, y'));
        case 'SHORT':
          return format(dateTime, t('E, MMM do, y'));
        default:
          return format(dateTime, t('EEEE, MMMM d'));
      }
    },
    formatKopyTrade: (date: any) => {
      return format(new Date(date), t('d MMM y'));
    },
    formatRewardHistory: (date: any) => {
      return formatInstant(new Date(date));
    },
    formatTooltip: (date: any) => {
      return format(new Date(date), t("E, d MMM y 'at' HH:mm"));
    },
  };
}
